/** Calendar — v1 parity (index.html local calendar engine + expenses-calendar-
 * materials.js calendarView), reading rec_calendar_events directly (Phase 7
 * SELECT policy) and writing whole-group replaces through crm2_apply_ops
 * (Phase 7 door: {s:'calendar', op:'group', k:<group>, d:[events]}).
 *
 * v1's model, verbatim:
 *   CAL_EVENTS = { group: [event…] } — the Calendar page uses group 'cal'
 *   (the Transactions page keeps its own 'showings' group — untouched here).
 *   event = {y, m(0-based), d, label, type, title, allDay, startTime,
 *            endDate, endTime, location, desc}
 *   label = allDay ? title : title + ' ' + fmtCalTime(startTime)
 *
 * The agenda merges THREE sources (v1 _calEventsAll):
 *   events   (kind 'event',   blue)   from CAL_EVENTS['cal']
 *   tasks    (kind 'task',    amber)  open tasks with a real due date
 *   closings (kind 'closing', emerald) deals with a t_closing date
 *
 * BETTER-THAN-V1 (Tom's 2026-07-17 "strengthen as you go"): v1 parses
 * "2026-07-20" with new Date(), which reads it as UTC midnight and can show
 * events a day early west of Greenwich. Our parser splits ISO dates into
 * LOCAL parts, so a Jul 20 closing is Jul 20 in Florida, full stop. */
import { idbGet, idbSet } from "../../lib/idb";
import type { RecOp } from "../../lib/outbox";
import { isTaskDone, pageAll, type TaskRec } from "../edits/api";
import type { Deal } from "../deals/api";

/** v1 CAL_TYPES, verbatim order; 'Other' is the form default. */
export const CAL_TYPES = ["Showing", "Inspection", "Appraisal", "Closing", "Meeting", "Task", "Deadline", "Other"];

/** v1 CAL_COLORS (tailwind hue names) → v2 chip class suffixes. */
export const CAL_COLORS: Record<string, string> = {
  Showing: "emerald", Inspection: "amber", Appraisal: "blue", Task: "violet",
  Closing: "rose", Meeting: "sky", Deadline: "red", Other: "slate",
};

export interface CalEvent {
  y: number;
  m: number; // 0-based like v1
  d: number;
  label: string;
  type?: string;
  title?: string;
  allDay?: boolean;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  location?: string;
  desc?: string;
  /** creator's user id — the event is HIS, and the reminder goes to him only
   * (Tom 2026-08-19: "who ever user making it it will be assign to him only") */
  by?: string;
  /** creator's display name, for the agenda chip */
  byName?: string;
  /** alert 1 hour before start (opt-in checkbox on the New Event modal);
   * all-day events remind at 8:00 AM ET that day. The backend scheduler
   * (notif_scheduler._scan_calendar_reminders) does the firing + snoozing. */
  remind?: boolean;
}

export type CalGroups = Record<string, CalEvent[]>;

/** The Calendar page's group, as in v1. */
export const CAL_GROUP = "cal";

const CACHE = "calendar_v1";

const num = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

/** v1 _calFmtTime, verbatim: '09:00' → '9:00 AM', '13:05' → '1:05 PM'. */
export function fmtCalTime(t: string | undefined | null): string {
  if (!t) return "";
  const p = String(t).split(":");
  let h = +(p[0] ?? 0);
  const m = p[1] ?? "00";
  const ap = h >= 12 ? "PM" : "AM";
  h = ((h + 11) % 12) + 1;
  return h + ":" + m + " " + ap;
}

/** v1 saveCalEvent's label rule. */
export function buildEventLabel(title: string, allDay: boolean, startTime?: string): string {
  return allDay ? title : title + (startTime ? " " + fmtCalTime(startTime) : "");
}

/** Tolerant single-event narrow: y/m/d must be real numbers (v1's agenda
 * requires them); everything else passes through. */
export function narrowCalEvent(v: unknown): CalEvent | null {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const y = num(o["y"]);
  const m = num(o["m"]);
  const d = num(o["d"]);
  if (y === null || m === null || d === null) return null;
  const ev: CalEvent = { y, m, d, label: str(o["label"]) ?? str(o["title"]) ?? "Event" };
  const t = str(o["type"]);
  if (t) ev.type = t;
  const ttl = str(o["title"]);
  if (ttl) ev.title = ttl;
  if (o["allDay"] === true) ev.allDay = true;
  const st = str(o["startTime"]);
  if (st) ev.startTime = st;
  const ed = str(o["endDate"]);
  if (ed) ev.endDate = ed;
  const et = str(o["endTime"]);
  if (et) ev.endTime = et;
  const loc = str(o["location"]);
  if (loc) ev.location = loc;
  const de = str(o["desc"]);
  if (de) ev.desc = de;
  const by = str(o["by"]);
  if (by) ev.by = by;
  const byName = str(o["byName"]);
  if (byName) ev.byName = byName;
  if (o["remind"] === true) ev.remind = true;
  return ev;
}

/** rec_calendar_events rows ({data:{g,ev}}) → v1's group map. Rows without a
 * string g / narrowable ev are dropped and counted, never rendered. */
export function groupsFromRows(rows: unknown[]): { groups: CalGroups; dropped: number } {
  const groups: CalGroups = {};
  let dropped = 0;
  for (const r of rows) {
    const o = r !== null && typeof r === "object" ? (r as Record<string, unknown>) : null;
    const data = o && o["data"] !== null && typeof o["data"] === "object" ? (o["data"] as Record<string, unknown>) : null;
    const g = data ? str(data["g"]) : null;
    const ev = data ? narrowCalEvent(data["ev"]) : null;
    if (!g || !ev) {
      dropped++;
      continue;
    }
    (groups[g] = groups[g] ?? []).push(ev);
  }
  return { groups, dropped };
}

export async function cachedCalendar(): Promise<CalGroups | null> {
  const c = await idbGet<{ at: number; groups: CalGroups }>(CACHE);
  return c && c.groups && typeof c.groups === "object" ? c.groups : null;
}

export async function fetchCalendar(token: string, signal?: AbortSignal): Promise<{ groups: CalGroups; dropped: number }> {
  // paged: PostgREST clamps limit to 1000 — a single shot silently truncates
  const rows = await pageAll<unknown>(token, "rec_calendar_events?select=nat_key,data&deleted_at=is.null&order=updated_at.asc,id.asc", signal);
  const res = groupsFromRows(Array.isArray(rows) ? rows : []);
  await idbSet(CACHE, { at: Date.now(), groups: res.groups });
  return res;
}

/** Whole-group replace — v1's only calendar write shape. */
export function opReplaceCalendarGroup(group: string, events: CalEvent[]): RecOp {
  return { s: "calendar", op: "group", k: group, d: events as unknown as Record<string, unknown>[] };
}

// ---- agenda merge (v1 _calEventsAll, pure + testable) ----------------------

export interface DayStamp {
  y: number;
  m: number; // 0-based
  d: number;
}

export interface CalItem {
  day: DayStamp;
  label: string;
  kind: "event" | "task" | "closing";
  /** original event when kind === 'event' (drawer/details) */
  ev?: CalEvent;
}

/** Day-serial for pure date math (no TZ, no DST): days since epoch-ish. */
export const daySerial = (s: DayStamp): number => Date.UTC(s.y, s.m, s.d) / 86400000;

/** Parse v1's date strings into LOCAL day parts.
 * Handles: 'Jul 20, 2026' (v1 display), '2026-07-20' (ISO, split manually —
 * BETTER-THAN-V1: no UTC day-shift), and anything else via Date fallback. */
export function parseDay(s: string | null | undefined): DayStamp | null {
  if (!s || typeof s !== "string") return null;
  const t = s.trim();
  if (!t || t === "—") return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (iso) {
    const y = +iso[1]!, m = +iso[2]! - 1, d = +iso[3]!;
    if (m >= 0 && m <= 11 && d >= 1 && d <= 31) return { y, m, d };
    return null;
  }
  const dt = new Date(t);
  if (Number.isNaN(dt.getTime())) return null;
  return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() };
}

/** v1 _calEventsAll, verbatim semantics + labels. */
export function mergeCalendarItems(
  groups: CalGroups,
  tasks: TaskRec[],
  deals: Deal[],
): CalItem[] {
  const out: CalItem[] = [];
  for (const e of groups[CAL_GROUP] ?? []) {
    out.push({ day: { y: e.y, m: e.m, d: e.d }, label: e.label || "Event", kind: "event", ev: e });
  }
  for (const t of tasks) {
    if (!t.due || t.due === "—" || isTaskDone(t.status)) continue;
    const day = parseDay(t.due);
    if (!day) continue;
    out.push({
      day,
      label: "Task: " + t.title + (t.deal ? " · " + String(t.deal).split(",")[0] : ""),
      kind: "task",
    });
  }
  for (const dl of deals) {
    const cd = dl.fields["t_closing"];
    const day = typeof cd === "string" ? parseDay(cd) : null;
    if (!day) continue;
    out.push({ day, label: "Closing: " + String(dl.address ?? "").split(",")[0], kind: "closing" });
  }
  return out;
}

export type CalRange = "today" | "tomorrow" | "week" | "month" | "grid";

/** v1 calAgendaHTML's inRange, on day serials (diff in whole days). */
export function inRange(day: DayStamp, range: CalRange, today: DayStamp): boolean {
  const diff = daySerial(day) - daySerial(today);
  if (range === "today") return diff === 0;
  if (range === "tomorrow") return diff === 1;
  if (range === "week") return diff >= 0 && diff < 7;
  if (range === "month") return day.m === today.m && day.y === today.y;
  return true;
}

/** Sorted + grouped agenda for one range: [{day, items[]}...] oldest first. */
export function agendaFor(items: CalItem[], range: CalRange, today: DayStamp): { day: DayStamp; items: CalItem[] }[] {
  const hit = items.filter((i) => inRange(i.day, range, today)).sort((a, b) => daySerial(a.day) - daySerial(b.day));
  const by = new Map<number, { day: DayStamp; items: CalItem[] }>();
  for (const i of hit) {
    const k = daySerial(i.day);
    let g = by.get(k);
    if (!g) {
      g = { day: i.day, items: [] };
      by.set(k, g);
    }
    g.items.push(i);
  }
  return [...by.values()];
}

// ---- month grid (v1 calMonthHTML math, pure) --------------------------------

export interface GridCell {
  day: number | null; // null = pad cell outside the month
  isToday: boolean;
}

/** 42 cells starting on the Sunday-week of the 1st — exactly v1's loop. */
export function monthGrid(y: number, m: number, today: DayStamp): GridCell[] {
  const startDow = new Date(y, m, 1).getDay();
  const dim = new Date(y, m + 1, 0).getDate();
  const cells: GridCell[] = [];
  for (let i = 0; i < 42; i++) {
    const dn = i - startDow + 1;
    const inMonth = dn >= 1 && dn <= dim;
    cells.push({
      day: inMonth ? dn : null,
      isToday: inMonth && dn === today.d && m === today.m && y === today.y,
    });
  }
  return cells;
}

/** 'July 2026' — v1's month header. */
export const monthTitle = (y: number, m: number): string =>
  new Date(y, m, 1).toLocaleString("en-US", { month: "long", year: "numeric" });

/** 'Thursday, Jul 17' — v1's agenda day header. */
export const dayHeader = (s: DayStamp): string =>
  new Date(s.y, s.m, s.d).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

export const todayStamp = (now: Date = new Date()): DayStamp => ({
  y: now.getFullYear(),
  m: now.getMonth(),
  d: now.getDate(),
});
